<template>
  <div class="leader-root">
    <div v-if="!accounts.length" class="leader-empty">
      <v-icon size="40" color="grey lighten-1">mdi-chart-bar</v-icon>
      <p class="leader-empty-title">No activity in this period</p>
      <p class="leader-empty-text">
        Rankings update when your team logs calls for the selected time range.
      </p>
    </div>

    <div v-else class="leader-content">
      <header class="leader-section-head">
        <h2 class="leader-section-title">Leaderboard</h2>
      </header>

      <div class="podium-arena">
        <p class="podium-arena-label">{{ periodMetricLabel }}</p>
        <div class="podium-stage">
          <div
            v-for="slot in podiumSlots"
            :key="'podium-' + slot.rank"
            class="podium-col"
            :class="'podium-col--' + slot.rank"
          >
            <article
              class="podium-card"
              :class="'podium-card--' + slot.rank"
            >
              <div v-if="slot.user" class="podium-card-body">
                <div class="podium-avatar-stack">
                  <v-icon
                    v-if="slot.rank === 1"
                    class="podium-crown"
                    color="#FFB300"
                    size="26"
                  >
                    mdi-crown
                  </v-icon>
                  <div
                    class="podium-avatar-wrap"
                    :class="'podium-avatar-wrap--' + slot.rank"
                  >
                    <v-avatar
                      :size="podiumAvatarSize(slot.rank)"
                      class="podium-avatar"
                    >
                      <v-img
                        v-if="showPhoto(slot.user)"
                        :src="getImage(slot.user.picture)"
                        alt=""
                        @error="onAvatarError(slot.user.id)"
                      />
                      <span
                        v-else
                        class="avatar-initial avatar-initial--podium"
                        >{{ initial(slot.user.name) }}</span
                      >
                    </v-avatar>
                    <span
                      class="podium-gem"
                      :class="'podium-gem--' + slot.rank"
                    >
                      <span class="podium-gem-num">{{ slot.rank }}</span>
                    </span>
                  </div>
                </div>
                <p class="podium-name" :title="slot.user.name">
                  {{ slot.user.name }}
                </p>
                <p
                  class="podium-stat"
                  :class="'podium-stat--' + slot.rank"
                >
                  <span class="podium-stat-value">{{
                    formatScore(slot.user)
                  }}</span>
                  <span class="podium-stat-label">{{
                    scoreUnitLabel(slot.user)
                  }}</span>
                </p>
                <p class="podium-meta">{{ slot.user.role || "Member" }}</p>
              </div>
              <div v-else class="podium-card-body podium-card-body--empty">
                <div class="podium-avatar-stack">
                  <div class="podium-avatar-wrap podium-avatar-wrap--ghost">
                    <div
                      class="podium-avatar-placeholder"
                      :style="{
                        width: podiumAvatarSize(slot.rank) + 'px',
                        height: podiumAvatarSize(slot.rank) + 'px',
                      }"
                    />
                    <span class="podium-gem podium-gem--ghost">
                      <span class="podium-gem-num">{{ slot.rank }}</span>
                    </span>
                  </div>
                </div>
                <p class="podium-name podium-name--muted">Open spot</p>
              </div>
              <div
                class="podium-card-foot"
                :class="'podium-card-foot--' + slot.rank"
                aria-hidden="true"
              />
            </article>
          </div>
        </div>
      </div>

      <header class="leader-section-head leader-section-head--table">
        <h2 class="leader-section-title">Full rankings</h2>
      </header>

      <v-card flat outlined class="rankings-card d-none d-sm-block">
        <div
          class="rankings-scroll"
          :class="{ 'rankings-scroll--dispo': useDispoRanking && rankingDispos.length }"
        >
        <v-data-table
          :headers="tableHeaders"
          :items="accounts"
          :items-per-page="10"
          class="rankings-table elevation-0"
          :class="{ 'rankings-table--dispo': useDispoRanking && rankingDispos.length }"
          :item-class="rowClass"
        >
          <template v-slot:item.rank="{ item }">
            <span
              class="rank-cell"
              :class="rankCellClass(item)"
            >
              {{ accountRank(item) }}
            </span>
          </template>
          <template v-slot:item.name="{ item }">
            <div class="name-cell">
              <v-avatar size="32" class="table-avatar">
                <v-img
                  v-if="showPhoto(item)"
                  :src="getImage(item.picture)"
                  alt=""
                  @error="onAvatarError(item.id)"
                />
                <span v-else class="avatar-initial avatar-initial--sm">{{
                  initial(item.name)
                }}</span>
              </v-avatar>
              <span class="name-cell-text">{{ item.name }}</span>
            </div>
          </template>
          <template v-slot:item.role="{ item }">
            <span class="role-text">{{ item.role }}</span>
          </template>
          <template
            v-if="tabtype === 'topCaller' && !dispoColumnMode"
            v-slot:item.primaryScore="{ item }"
          >
            <span class="num-cell num-cell--emphasis">{{
              primaryValue(item)
            }}</span>
          </template>
          <template
            v-for="(dispo, dispoIndex) in rankingDispos"
            v-slot:[dispoSlotName(dispoIndex)]="{ item }"
          >
            <div
              :key="'dispo-cell-' + dispoIndex"
              class="dispo-count-cell"
              :class="{ 'dispo-count-cell--zero': dispoCount(item, dispoIndex) === 0 }"
            >
              <span class="dispo-count-cell__value">{{
                dispoCount(item, dispoIndex)
              }}</span>
            </div>
          </template>
          <template
            v-if="tabtype === 'topCaller'"
            v-slot:item.connectedCalls="{ item }"
          >
            <span class="num-cell">{{ item.connectedCalls }}</span>
          </template>
          <template
            v-if="tabtype === 'topCaller'"
            v-slot:item.notConnectedCalls="{ item }"
          >
            <span class="num-cell num-cell--muted">{{
              item.notConnectedCalls
            }}</span>
          </template>
          <template v-slot:item.timeSpentOnCall="{ item }">
            <span class="num-cell num-cell--emphasis">{{
              item.timeSpentOnCall
            }}</span>
          </template>
          <template v-slot:item.presentCount="{ item }">
            <span class="num-cell">{{ item.presentCount }}</span>
          </template>
        </v-data-table>
        </div>
      </v-card>

      <div class="mobile-list d-sm-none">
        <div
          v-for="(item, index) in accounts"
          :key="item.id || index"
          class="mobile-row"
        >
          <span
            class="rank-cell rank-cell--mobile"
            :class="rankCellClass(item)"
          >
            {{ accountRank(item) }}
          </span>
          <v-avatar size="36" class="table-avatar">
            <v-img
              v-if="showPhoto(item)"
              :src="getImage(item.picture)"
              alt=""
              @error="onAvatarError(item.id)"
            />
            <span v-else class="avatar-initial avatar-initial--sm">{{
              initial(item.name)
            }}</span>
          </v-avatar>
          <div class="mobile-body">
            <div class="mobile-name">{{ item.name }}</div>
            <div class="mobile-role">{{ item.role }}</div>
          </div>
          <div class="mobile-metric">
            <template v-if="dispoColumnMode">
              <div
                v-for="(dispo, dispoIndex) in rankingDispos"
                :key="dispoIndex"
                class="mobile-dispo-line"
              >
                <span class="mobile-dispo-name">{{ dispo }}</span>
                <span class="mobile-dispo-count">{{
                  dispoCount(item, dispoIndex)
                }}</span>
              </div>
            </template>
            <template v-else>
              <span class="num-cell num-cell--emphasis">{{
                tabtype === "topActive"
                  ? item.timeSpentOnCall
                  : primaryValue(item)
              }}</span>
              <span class="mobile-metric-unit">{{
                tabtype === "topActive" ? "min" : scoreUnit(item)
              }}</span>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const _ = require("lodash");

export default {
  props: {
    tabtype: String,
    accounts: {
      type: Array,
      default: () => [],
    },
    useDispoRanking: {
      type: Boolean,
      default: false,
    },
    rankingDispoLabel: {
      type: String,
      default: "Calls",
    },
    rankingDispos: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      brokenAvatars: {},
      headersCallerBase: [
        {
          text: "Rank",
          value: "rank",
          sortable: false,
          width: "64px",
          align: "start",
        },
        { text: "Name", value: "name", sortable: false },
        { text: "Role", value: "role", sortable: false, width: "128px" },
        {
          text: "Calls",
          shortText: "Calls",
          value: "primaryScore",
          align: "end",
          sortable: false,
          width: "88px",
        },
        {
          text: "Connected",
          shortText: "Conn.",
          value: "connectedCalls",
          align: "end",
          sortable: false,
          width: "96px",
        },
        {
          text: "Not connected",
          shortText: "N/C",
          value: "notConnectedCalls",
          align: "end",
          sortable: false,
          width: "112px",
        },
        {
          text: "Active days",
          shortText: "Days",
          value: "presentCount",
          align: "end",
          sortable: false,
          width: "96px",
        },
      ],
      headersActivity: [
        {
          text: "Rank",
          value: "rank",
          sortable: false,
          width: "64px",
          align: "start",
        },
        { text: "Name", value: "name", sortable: false },
        { text: "Role", value: "role", sortable: false, width: "128px" },
        {
          text: "Minutes",
          shortText: "Min",
          value: "timeSpentOnCall",
          align: "end",
          sortable: false,
          width: "88px",
        },
        {
          text: "Active days",
          shortText: "Days",
          value: "presentCount",
          align: "end",
          sortable: false,
          width: "96px",
        },
      ],
    };
  },
  computed: {
    podiumSlots() {
      const list = this.accounts;
      return [
        { rank: 2, user: list[1] || null },
        { rank: 1, user: list[0] || null },
        { rank: 3, user: list[2] || null },
      ];
    },
    periodMetricLabel() {
      if (this.tabtype === "topActive") {
        return "Ranked by talk time on calls";
      }
      if (this.useDispoRanking) {
        return `Ranked by ${this.rankingDispoLabel}`;
      }
      return "Ranked by total calls";
    },
    dispoColumnMode() {
      return (
        this.tabtype === "topCaller" &&
        this.useDispoRanking &&
        this.rankingDispos.length > 0
      );
    },
    tableHeaders() {
      let headers =
        this.tabtype === "topActive"
          ? _.cloneDeep(this.headersActivity)
          : _.cloneDeep(this.headersCallerBase);

      if (this.dispoColumnMode) {
        const rankCol = headers.find((h) => h.value === "rank");
        const nameCol = headers.find((h) => h.value === "name");
        const statColMeta = {
          connectedCalls: {
            width: "108px",
            class: "leader-stat-col",
            cellClass: "leader-stat-col",
          },
          notConnectedCalls: {
            width: "124px",
            class: "leader-stat-col",
            cellClass: "leader-stat-col",
          },
          presentCount: {
            width: "104px",
            class: "leader-stat-col",
            cellClass: "leader-stat-col",
          },
        };
        const tailCols = headers
          .filter(
            (h) =>
              !["rank", "name", "role", "primaryScore"].includes(h.value),
          )
          .map((h) => {
            const meta = statColMeta[h.value];
            return meta ? { ...h, ...meta } : h;
          });
        headers = [
          {
            ...rankCol,
            width: "80px",
            class: "leader-sticky-col leader-sticky-col--rank",
            cellClass: "leader-sticky-col leader-sticky-col--rank",
          },
          {
            ...nameCol,
            width: "240px",
            class: "leader-sticky-col leader-sticky-col--name",
            cellClass: "leader-sticky-col leader-sticky-col--name",
          },
          ...this.rankingDispos.map((title, index) => ({
            text: title,
            shortText:
              title.length > 14 ? title.substring(0, 12) + "…" : title,
            value: `dispoCol${index}`,
            align: "center",
            sortable: false,
            width: "120px",
            class: "dispo-header-col",
            cellClass: "dispo-data-col",
          })),
          ...tailCols,
        ];
      } else if (this.tabtype === "topCaller" && this.useDispoRanking) {
        const scoreCol = headers.find((h) => h.value === "primaryScore");
        if (scoreCol) {
          scoreCol.text = "Score";
          scoreCol.shortText = "Score";
        }
      }

      if (this.$vuetify && this.$vuetify.breakpoint.smAndDown) {
        headers = headers.map((h) => ({
          ...h,
          text: h.shortText || h.text,
        }));
      }
      return headers;
    },
  },
  methods: {
    accountRank(item) {
      const idx = this.accounts.findIndex((a) => a.id === item.id);
      return idx >= 0 ? idx + 1 : "—";
    },
    rowClass(item) {
      const r = this.accountRank(item);
      if (r === 1) return "rankings-row-top1";
      if (r === 2) return "rankings-row-top2";
      if (r === 3) return "rankings-row-top3";
      return "";
    },
    rankCellClass(item) {
      const r = this.accountRank(item);
      if (r >= 1 && r <= 3) {
        return "rank-cell--podium rank-cell--podium-" + r;
      }
      if (typeof r === "number" && r > 3) {
        return "rank-cell--rest";
      }
      return "";
    },
    dispoSlotName(index) {
      return `item.dispoCol${index}`;
    },
    dispoCount(item, index) {
      const key = `dispoCol${index}`;
      const val = item[key];
      return val != null ? val : 0;
    },
    onAvatarError(id) {
      if (id) {
        this.$set(this.brokenAvatars, id, true);
      }
    },
    showPhoto(user) {
      return (
        user &&
        user.pictureId &&
        user.picture &&
        !this.brokenAvatars[user.id]
      );
    },
    initial(name) {
      if (!name) return "?";
      return name.substring(0, 1).toUpperCase();
    },
    primaryValue(item) {
      if (this.useDispoRanking) {
        return item.dispoScore != null ? item.dispoScore : 0;
      }
      return item.count != null ? item.count : 0;
    },
    formatScore(item) {
      if (this.tabtype === "topActive") {
        return item.timeSpentOnCall != null ? item.timeSpentOnCall : 0;
      }
      return this.primaryValue(item);
    },
    scoreUnit(item) {
      return this.scoreUnitLabel(item);
    },
    scoreUnitLabel(item) {
      const n = this.formatScore(item);
      if (this.tabtype === "topActive") {
        return n === 1 ? "minute on calls" : "minutes on calls";
      }
      if (this.useDispoRanking) {
        return this.rankingDispoLabel;
      }
      return n === 1 ? "call" : "calls";
    },
    podiumAvatarSize(rank) {
      const compact =
        this.$vuetify && this.$vuetify.breakpoint.xsOnly;
      if (rank === 1) return compact ? 64 : 80;
      return compact ? 56 : 68;
    },
    getImage(pic) {
      return `${this.$store.state.ENDPOINT}/storages/get/${pic}?access_token=${this.$store.getters.user.token}`;
    },
  },
};
</script>

<style scoped>
.leader-root {
  --gd-green: #37cf37;
  --gd-green-dark: #31a848;
  --gd-green-deep: #2ba82b;
  --gd-gold: #ffb300;
  --gd-gold-deep: #e6a800;
  --leader-text: rgba(0, 0, 0, 0.87);
  --leader-text-secondary: rgba(0, 0, 0, 0.55);
  --leader-border: rgba(0, 0, 0, 0.1);
  --leader-surface: #ffffff;
  --leader-accent: #31a848;
  --podium-bg: linear-gradient(
    165deg,
    rgba(55, 207, 55, 0.14) 0%,
    rgba(49, 168, 72, 0.06) 45%,
    rgba(255, 255, 255, 0) 100%
  );
}

.theme--dark .leader-root {
  --leader-text: rgba(255, 255, 255, 0.92);
  --leader-text-secondary: rgba(255, 255, 255, 0.58);
  --leader-border: rgba(255, 255, 255, 0.1);
  --leader-surface: #1e1e1e;
  --leader-accent: #66bb6a;
  --podium-bg: linear-gradient(
    165deg,
    rgba(55, 207, 55, 0.18) 0%,
    rgba(30, 60, 30, 0.85) 55%,
    #1a1f1a 100%
  );
}

.leader-content {
  padding: 8px 0 12px;
  overflow: visible;
}

.leader-section-head {
  margin-bottom: 14px;
}

.leader-section-head--table {
  margin-top: 28px;
  margin-bottom: 12px;
}

.leader-section-title {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--leader-text);
  margin: 0;
  line-height: 1.3;
}

.leader-section-sub {
  font-size: 13px;
  color: var(--leader-text-secondary);
  margin: 4px 0 0;
  line-height: 1.4;
}

/* Podium */
.podium-arena {
  background: var(--podium-bg);
  border: 1px solid rgba(49, 168, 72, 0.2);
  border-radius: 16px;
  padding: 14px 10px 14px;
  margin-bottom: 12px;
  overflow: visible;
}

.theme--dark .podium-arena {
  border-color: rgba(102, 187, 106, 0.25);
}

.podium-arena-label {
  text-align: center;
  font-size: 13px;
  color: var(--leader-text-secondary);
  margin: 0 0 14px;
  line-height: 1.4;
}

.podium-stage {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 10px;
  max-width: 720px;
  margin: 0 auto;
}

@media (min-width: 600px) {
  .podium-stage {
    gap: 14px;
    padding: 0 8px;
  }
}

.podium-col {
  flex: 1 1 0;
  min-width: 0;
  max-width: 220px;
  display: flex;
}

.podium-col--1 {
  order: 2;
}

.podium-col--2 {
  order: 1;
}

.podium-col--3 {
  order: 3;
}

.podium-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
}

.theme--dark .podium-card {
  background: rgba(30, 30, 30, 0.95);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
}

.podium-card--1 {
  transform: translateY(-6px);
  border-color: rgba(255, 179, 0, 0.45);
  box-shadow: 0 8px 22px rgba(255, 179, 0, 0.18);
}

.podium-card-body {
  text-align: center;
  padding: 14px 8px 10px;
  flex: 1 1 auto;
  min-height: 0;
}

.podium-card-body--empty {
  padding-bottom: 16px;
}

.podium-avatar-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: 8px;
}

.podium-crown {
  margin-bottom: 4px;
  filter: drop-shadow(0 2px 4px rgba(255, 179, 0, 0.35));
}

.podium-avatar-wrap {
  position: relative;
  display: inline-block;
  margin: 0 auto 10px;
  border-radius: 50%;
  padding: 4px;
}

.podium-avatar-wrap--1 {
  background: linear-gradient(145deg, #ffd54f, #ffb300);
  box-shadow: 0 8px 24px rgba(255, 179, 0, 0.35);
}

.podium-avatar-wrap--2 {
  background: linear-gradient(145deg, #69f06a, var(--gd-green-dark));
  box-shadow: 0 6px 18px rgba(55, 207, 55, 0.3);
}

.podium-avatar-wrap--3 {
  background: linear-gradient(145deg, #5ec75e, var(--gd-green-deep));
  box-shadow: 0 4px 14px rgba(43, 168, 43, 0.28);
}

.podium-avatar-wrap--ghost {
  background: rgba(0, 0, 0, 0.06);
  box-shadow: none;
}

.theme--dark .podium-avatar-wrap--ghost {
  background: rgba(255, 255, 255, 0.08);
}

.podium-avatar {
  border: 3px solid var(--leader-surface) !important;
}

.podium-avatar >>> .v-image__image {
  object-fit: cover;
}

.podium-avatar-placeholder {
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
}

.podium-gem {
  position: absolute;
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  color: #fff;
  border: 2px solid var(--leader-surface);
  line-height: 1;
}

.podium-gem--1 {
  background: linear-gradient(135deg, #ffd54f, #ffb300);
  color: #5d4200;
}

.podium-gem--2 {
  background: linear-gradient(135deg, #69f06a, var(--gd-green-dark));
}

.podium-gem--3 {
  background: linear-gradient(135deg, #5ec75e, var(--gd-green-deep));
}

.podium-gem--ghost {
  background: rgba(0, 0, 0, 0.15);
}

.podium-gem-num {
  display: block;
  transform: rotate(-45deg);
  font-variant-numeric: tabular-nums;
}

.podium-name {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--leader-text);
  line-height: 1.35;
  padding: 0 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

@media (min-width: 600px) {
  .podium-name {
    font-size: 14px;
    -webkit-line-clamp: 1;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: block;
  }
}

.podium-name--muted {
  color: var(--leader-text-secondary);
  font-weight: 500;
}

.podium-stat {
  margin: 6px 0 0;
  line-height: 1.2;
}

.podium-stat-value {
  font-size: 24px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.podium-card--1 .podium-stat-value {
  font-size: 28px;
}

.podium-stat-label {
  font-size: 14px;
  font-weight: 600;
  margin-left: 4px;
  color: var(--leader-text-secondary);
}

.podium-stat--1 .podium-stat-value {
  color: var(--gd-gold-deep);
}

.podium-stat--2 .podium-stat-value,
.podium-stat--3 .podium-stat-value {
  color: var(--gd-green-dark);
}

.theme--dark .podium-stat--1 .podium-stat-value {
  color: #ffd54f;
}

.theme--dark .podium-stat--2 .podium-stat-value,
.theme--dark .podium-stat--3 .podium-stat-value {
  color: var(--gd-green);
}

.podium-meta {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--leader-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.podium-card-foot {
  width: 100%;
  flex-shrink: 0;
}

.podium-card-foot--1 {
  height: 36px;
  background: linear-gradient(
    90deg,
    var(--gd-green-dark) 0%,
    #ffb300 50%,
    var(--gd-green-dark) 100%
  );
}

.podium-card-foot--2 {
  height: 24px;
  background: linear-gradient(
    180deg,
    rgba(55, 207, 55, 0.85) 0%,
    var(--gd-green-deep) 100%
  );
}

.podium-card-foot--3 {
  height: 16px;
  background: linear-gradient(
    180deg,
    rgba(49, 168, 72, 0.75) 0%,
    var(--gd-green-deep) 100%
  );
}

@media (max-width: 599px) {
  .podium-card--1 {
    transform: translateY(-4px);
  }

  .podium-stat-value {
    font-size: 20px;
  }

  .podium-card--1 .podium-stat-value {
    font-size: 24px;
  }

  .podium-stat-label {
    font-size: 12px;
  }

  .podium-card-foot--1 {
    height: 28px;
  }

  .podium-card-foot--2 {
    height: 20px;
  }

  .podium-card-foot--3 {
    height: 14px;
  }
}

.avatar-initial {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 600;
  color: var(--leader-text-secondary);
  background: rgba(0, 0, 0, 0.06);
}

.avatar-initial--podium {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
  color: var(--gd-green-deep);
}

.theme--dark .avatar-initial {
  background: rgba(255, 255, 255, 0.08);
}

.theme--dark .avatar-initial--podium {
  background: rgba(55, 207, 55, 0.15);
  color: #a5d6a7;
}

.avatar-initial--sm {
  font-size: 13px;
}

/* Table */
.rankings-card {
  border-color: var(--leader-border) !important;
  border-radius: 8px !important;
  overflow: hidden;
  background: var(--leader-surface) !important;
}

.rank-cell {
  font-size: 13px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--leader-text-secondary);
}

.rank-cell--podium {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 12px;
  color: #fff;
}

.rank-cell--podium-1 {
  background: linear-gradient(135deg, #ffd54f, #ffb300);
  color: #5d4200;
}

.rank-cell--podium-2 {
  background: linear-gradient(135deg, #69f06a, var(--gd-green-dark));
}

.rank-cell--podium-3 {
  background: linear-gradient(135deg, #5ec75e, var(--gd-green-deep));
}

.rank-cell--rest {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
  background: #eceff1;
}

.theme--dark .rank-cell--rest {
  background: #37474f;
  color: rgba(255, 255, 255, 0.75);
}

.rankings-scroll {
  width: 100%;
}

.rankings-scroll--dispo {
  overflow-x: visible;
}

.dispo-count-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  box-sizing: border-box;
}

.dispo-count-cell__value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 32px;
  padding: 4px 12px;
  border-radius: 8px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background: rgba(55, 207, 55, 0.14);
  color: var(--gd-green-deep);
}

.dispo-count-cell--zero .dispo-count-cell__value {
  background: #eceff1;
  color: rgba(0, 0, 0, 0.45);
  font-weight: 600;
}

.theme--dark .dispo-count-cell--zero .dispo-count-cell__value {
  background: #37474f;
  color: rgba(255, 255, 255, 0.45);
}

.mobile-dispo-line {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  font-size: 11px;
  line-height: 1.3;
}

.mobile-dispo-name {
  color: var(--leader-text-secondary);
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-dispo-count {
  font-weight: 700;
  min-width: 24px;
  text-align: right;
}

.name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  max-width: 100%;
  padding-right: 4px;
}

.name-cell-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--leader-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-avatar {
  flex-shrink: 0;
  border: 1px solid var(--leader-border) !important;
}

.table-avatar >>> .v-image__image {
  object-fit: cover;
}

.role-text {
  font-size: 13px;
  color: var(--leader-text-secondary);
}

.num-cell {
  font-size: 14px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--leader-text);
}

.num-cell--emphasis {
  font-weight: 600;
}

.num-cell--muted {
  color: var(--leader-text-secondary);
}

.leader-empty {
  text-align: center;
  padding: 56px 24px;
  border: 1px solid var(--leader-border);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.02);
}

.theme--dark .leader-empty {
  background: rgba(255, 255, 255, 0.03);
}

.leader-empty-title {
  font-size: 15px;
  font-weight: 600;
  margin: 16px 0 6px;
  color: var(--leader-text);
}

.leader-empty-text {
  font-size: 13px;
  color: var(--leader-text-secondary);
  margin: 0 auto;
  max-width: 340px;
  line-height: 1.5;
}

/* Mobile list */
.mobile-list {
  border: 1px solid var(--leader-border);
  border-radius: 8px;
  overflow: hidden;
  background: var(--leader-surface);
}

.mobile-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--leader-border);
}

.mobile-row:last-child {
  border-bottom: none;
}

.rank-cell--mobile {
  width: 24px;
  flex-shrink: 0;
  text-align: center;
}

.mobile-body {
  flex: 1;
  min-width: 0;
}

.mobile-name {
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-role {
  font-size: 12px;
  color: var(--leader-text-secondary);
  margin-top: 2px;
}

.mobile-metric {
  text-align: right;
  flex-shrink: 0;
}

.mobile-metric-unit {
  display: block;
  font-size: 10px;
  color: var(--leader-text-secondary);
  text-transform: lowercase;
}
</style>

<style>
.rankings-table.v-data-table {
  background: transparent !important;
}

.rankings-table--dispo.v-data-table {
  --leader-rank-col-w: 80px;
  --leader-name-col-w: 240px;
}

.rankings-scroll--dispo .rankings-table--dispo.v-data-table > .v-data-table__wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.rankings-scroll--dispo .rankings-table--dispo.v-data-table > .v-data-table__wrapper > table {
  width: max-content;
  min-width: 100%;
}

.rankings-table--dispo.v-data-table thead th.leader-sticky-col--rank,
.rankings-table--dispo.v-data-table tbody td.leader-sticky-col--rank {
  position: sticky;
  left: 0;
  z-index: 6;
  width: var(--leader-rank-col-w) !important;
  min-width: var(--leader-rank-col-w) !important;
  max-width: var(--leader-rank-col-w) !important;
  background: #fafafa !important;
  overflow: hidden;
}

.rankings-table--dispo.v-data-table thead th.leader-sticky-col--rank {
  z-index: 8;
}

.rankings-table--dispo.v-data-table thead th.leader-sticky-col--name,
.rankings-table--dispo.v-data-table tbody td.leader-sticky-col--name {
  position: sticky;
  left: var(--leader-rank-col-w);
  z-index: 6;
  width: var(--leader-name-col-w) !important;
  min-width: var(--leader-name-col-w) !important;
  max-width: var(--leader-name-col-w) !important;
  background: #fafafa !important;
  overflow: hidden;
  box-shadow: 4px 0 12px -6px rgba(0, 0, 0, 0.22);
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}

.rankings-table--dispo.v-data-table thead th.leader-sticky-col--name {
  z-index: 8;
}

.rankings-table--dispo.v-data-table tbody td.dispo-data-col,
.rankings-table--dispo.v-data-table thead th.dispo-header-col {
  z-index: 1;
  position: relative;
  padding-left: 12px !important;
  padding-right: 12px !important;
}

.theme--dark .rankings-table--dispo.v-data-table thead th.leader-sticky-col--rank,
.theme--dark .rankings-table--dispo.v-data-table tbody td.leader-sticky-col--rank,
.theme--dark .rankings-table--dispo.v-data-table thead th.leader-sticky-col--name,
.theme--dark .rankings-table--dispo.v-data-table tbody td.leader-sticky-col--name {
  background: #252525 !important;
  border-right-color: rgba(255, 255, 255, 0.1);
}

.rankings-table--dispo.v-data-table tbody tr.rankings-row-top1 td.leader-sticky-col--rank,
.rankings-table--dispo.v-data-table tbody tr.rankings-row-top1 td.leader-sticky-col--name {
  background: #fff8eb !important;
}

.rankings-table--dispo.v-data-table tbody tr.rankings-row-top2 td.leader-sticky-col--rank,
.rankings-table--dispo.v-data-table tbody tr.rankings-row-top2 td.leader-sticky-col--name,
.rankings-table--dispo.v-data-table tbody tr.rankings-row-top3 td.leader-sticky-col--rank,
.rankings-table--dispo.v-data-table tbody tr.rankings-row-top3 td.leader-sticky-col--name {
  background: #f3fbf3 !important;
}

.theme--dark .rankings-table--dispo.v-data-table tbody tr.rankings-row-top1 td.leader-sticky-col--rank,
.theme--dark .rankings-table--dispo.v-data-table tbody tr.rankings-row-top1 td.leader-sticky-col--name {
  background: #2e2818 !important;
}

.theme--dark .rankings-table--dispo.v-data-table tbody tr.rankings-row-top2 td.leader-sticky-col--rank,
.theme--dark .rankings-table--dispo.v-data-table tbody tr.rankings-row-top2 td.leader-sticky-col--name,
.theme--dark .rankings-table--dispo.v-data-table tbody tr.rankings-row-top3 td.leader-sticky-col--rank,
.theme--dark .rankings-table--dispo.v-data-table tbody tr.rankings-row-top3 td.leader-sticky-col--name {
  background: #1e2a1e !important;
}

.rankings-table--dispo.v-data-table tbody tr:hover td.leader-sticky-col--rank,
.rankings-table--dispo.v-data-table tbody tr:hover td.leader-sticky-col--name {
  background: #f5f5f5 !important;
}

.theme--dark .rankings-table--dispo.v-data-table tbody tr:hover td.leader-sticky-col--rank,
.theme--dark .rankings-table--dispo.v-data-table tbody tr:hover td.leader-sticky-col--name {
  background: #2c2c2c !important;
}

.rankings-table--dispo.v-data-table thead th.dispo-header-col {
  white-space: normal;
  word-break: break-word;
  line-height: 1.3;
  font-size: 11px !important;
  vertical-align: bottom;
  padding: 8px 12px 12px !important;
  min-width: 108px;
}

.rankings-table--dispo.v-data-table thead th.leader-stat-col,
.rankings-table--dispo.v-data-table tbody td.leader-stat-col {
  white-space: nowrap;
  text-align: right !important;
  padding-left: 16px !important;
  padding-right: 16px !important;
  box-sizing: border-box;
}

.rankings-table--dispo.v-data-table thead th.leader-stat-col {
  font-size: 12px !important;
  line-height: 1.2;
  height: auto !important;
  min-height: 44px;
  vertical-align: middle;
}

.rankings-table--dispo.v-data-table .v-data-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 8px;
  margin-top: 4px;
}

.rankings-table.v-data-table thead th {
  font-size: 12px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  color: var(--leader-text-secondary, rgba(0, 0, 0, 0.6)) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
  height: 44px !important;
  background: #fafafa !important;
}

.theme--dark .rankings-table.v-data-table thead th {
  background: #252525 !important;
  border-bottom-color: rgba(255, 255, 255, 0.12) !important;
}

.rankings-table.v-data-table tbody td {
  font-size: 14px !important;
  height: 52px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
}

.theme--dark .rankings-table.v-data-table tbody td {
  border-bottom-color: rgba(255, 255, 255, 0.06) !important;
}

.rankings-table.v-data-table tbody tr.rankings-row-top1 {
  background: rgba(255, 179, 0, 0.08) !important;
}

.rankings-table.v-data-table tbody tr.rankings-row-top2 {
  background: rgba(55, 207, 55, 0.07) !important;
}

.rankings-table.v-data-table tbody tr.rankings-row-top3 {
  background: rgba(49, 168, 72, 0.05) !important;
}

.theme--dark .rankings-table.v-data-table tbody tr.rankings-row-top1 {
  background: rgba(255, 179, 0, 0.1) !important;
}

.theme--dark .rankings-table.v-data-table tbody tr.rankings-row-top2,
.theme--dark .rankings-table.v-data-table tbody tr.rankings-row-top3 {
  background: rgba(102, 187, 106, 0.08) !important;
}

.rankings-table.v-data-table tbody tr:hover {
  background: rgba(0, 0, 0, 0.02) !important;
}

.theme--dark .rankings-table.v-data-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.04) !important;
}

.rankings-table.v-data-table .v-data-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  font-size: 12px;
}

.theme--dark .rankings-table.v-data-table .v-data-footer {
  border-top-color: rgba(255, 255, 255, 0.08);
}
</style>
